import React from "react";
import { useLang } from "@/i18n";
import { stages, orderStage, dateLabel, carrierName, trackingLink } from "@/lib/fulfillment";

export function OrderTimeline({ order }) {
  const { t, lang } = useLang();
  const entries = [["created_at", "placed"], ["paid_at", "paymentConfirmed"], ["preparing_at", "preparing"], ["ready_to_ship_at", "ready_to_ship"], ["shipped_at", "shipped"], ["delivered_at", "delivered"], ["cancelled_at", "cancelled"]]
    .filter(([key]) => dateLabel(order[key], lang)).sort((a, b) => Date.parse(order[a[0]]) - Date.parse(order[b[0]]));
  if (!entries.length) return null;
  return <section className="mt-4 rounded-xl border border-border bg-white p-4" data-testid="order-timeline">
    <h2 className="mb-3 font-bold">{t("orders.timeline")}</h2>
    <ol className="space-y-3">{entries.map(([key, label]) => <li key={key} className="flex flex-wrap justify-between gap-1 text-sm">
      <span>{t("orders." + label)}{key === "shipped_at" && order.tracking_carrier ? ` · ${carrierName(order.tracking_carrier, t)}` : ""}</span><time dateTime={order[key]}>{dateLabel(order[key], lang)}</time>
    </li>)}</ol>
  </section>;
}
export function ShipmentTracking({ order }) {
  const { t } = useLang();
  const link = trackingLink(order);
  if (!order.tracking_number && !["shipped", "delivered"].includes(orderStage(order))) return null;
  return <section className="mt-4 space-y-2 rounded-xl border border-border bg-white p-4 text-sm" data-testid="shipment-tracking">
    {order.tracking_number ? <>
      <p>{t("orders.carrier")}: <strong>{carrierName(order.tracking_carrier, t)}</strong></p>
      <p className="break-all">{t("orders.tracking")}: <strong>{order.tracking_number}</strong></p>
      {link ? <a className="btn-gold inline-flex min-h-11 items-center rounded-full px-5 font-bold" href={link} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{t("orders.track")}</a> : <p>{t("orders.otherHint")}</p>}
    </> : <p>{t("orders.trackingPending")}</p>}
  </section>;
}
export default function OrderProgress({ order }) {
  const { t } = useLang();
  const current = orderStage(order), index = stages.indexOf(current);
  return <section className="mt-4 rounded-xl border border-border bg-white p-4" data-testid="order-progress">
    <h2 className="mb-3 font-bold">{t("orders.progress")}</h2>
    {current === "cancelled" ? <p className="font-bold text-red-700">{t("orders.cancelled")}{order.legacy_refunded ? ` · ${t("orders.refunded")}` : ""}</p> : index < 0 ? <p>{t("orders.unknown")}</p> :
      <ol className="space-y-3">{stages.map((step, i) => {
        const complete = (i < index || current === "delivered") && (i !== 0 || order.payment_status === "paid");
        return <li key={step} aria-current={i === index ? "step" : undefined} data-completed={complete} className={`flex items-center gap-3 text-sm ${i === index ? "font-bold text-navy" : i < index ? "text-palm-500" : "text-navy/60"}`}>
          <span aria-hidden="true" className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-current">{complete ? "✓" : i + 1}</span>
          <span>{t("orders." + (step === "new" ? order.payment_status === "paid" ? "confirmed" : "placed" : step))}{i === index && <span className="block text-xs">{t("orders.current")}</span>}</span>
        </li>;
      })}</ol>}
  </section>;
}
